import React, { useState } from "react";

const move = (list, from, to) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Drag-to-reorder list (native HTML5 drag and drop, no library).
 * Only the handle starts a drag; ↑/↓ buttons give a keyboard alternative.
 *
 * renderItem(item, index, { handle, moveUp, moveDown }) — put `handle` where
 * the grip should be; moveUp/moveDown are null at the ends.
 */
const SortableList = ({
  items,
  onReorder,
  renderItem,
  readOnly,
  className = "",
}) => {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const reset = () => {
    setDragId(null);
    setOverId(null);
  };

  return (
    <div className={`sortable-list ${className}`}>
      {items.map((item, index) => {
        const id = item._id;
        const handle = readOnly ? null : (
          <span
            className="sortable-handle"
            draggable
            title="Drag to reorder"
            aria-hidden="true"
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", id);
              const row = e.currentTarget.closest(".sortable-row");
              if (row) e.dataTransfer.setDragImage(row, 24, 24);
              setDragId(id);
            }}
            onDragEnd={reset}
          >
            <i className="bi bi-grip-vertical"></i>
          </span>
        );
        return (
          <div
            key={id}
            className={`sortable-row ${dragId === id ? "is-dragging" : ""} ${overId === id && dragId !== id ? "is-over" : ""}`}
            onDragOver={(e) => {
              if (!dragId) return; // a drag from another list
              e.preventDefault();
              e.stopPropagation();
              if (overId !== id) setOverId(id);
            }}
            onDrop={(e) => {
              if (!dragId) return;
              e.preventDefault();
              e.stopPropagation();
              const from = items.findIndex((x) => x._id === dragId);
              if (from !== -1 && from !== index)
                onReorder(move(items, from, index));
              reset();
            }}
          >
            {renderItem(item, index, {
              handle,
              moveUp:
                !readOnly && index > 0
                  ? () => onReorder(move(items, index, index - 1))
                  : null,
              moveDown:
                !readOnly && index < items.length - 1
                  ? () => onReorder(move(items, index, index + 1))
                  : null,
            })}
          </div>
        );
      })}
    </div>
  );
};

/** ↑ ↓ ✕ buttons used next to sortable rows. */
export const RowTools = ({ moveUp, moveDown, onRemove, label, readOnly }) =>
  readOnly ? null : (
    <div className="row-tools">
      <button
        type="button"
        className="btn btn-sm btn-light"
        onClick={moveUp}
        disabled={!moveUp}
        aria-label={`Move ${label} up`}
        title="Move up"
      >
        <i className="bi bi-arrow-up"></i>
      </button>
      <button
        type="button"
        className="btn btn-sm btn-light"
        onClick={moveDown}
        disabled={!moveDown}
        aria-label={`Move ${label} down`}
        title="Move down"
      >
        <i className="bi bi-arrow-down"></i>
      </button>
      {onRemove && (
        <button
          type="button"
          className="btn btn-sm btn-light text-danger"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          title="Remove"
        >
          <i className="bi bi-trash"></i>
        </button>
      )}
    </div>
  );

export default SortableList;
